import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { PaymentTypes, PaymentTypesType } from 'services/api/PaymentApi/types/common'
import { IGetCryptoEstimateAmountPayload } from 'services/api/PaymentApi/types/types'

import { mobCashSlug } from 'shared/constants/payment'
import { LocalePaymentType } from 'shared/locale/en-US'
import { IPaymentPayload } from 'shared/models/Payments'

import {
  clearConvertedAmountData,
  clearDepositCreated,
  clearScreenshotsData,
  createDeposit,
  createPayout,
  getCryptoEstimateAmount,
} from 'features/payment/actions'
import {
  selectConvertedAmountData,
  selectConvertedAmountDataComm,
  selectCreateDepositComm,
  selectCreatePayoutComm,
  selectDepositData,
} from 'features/payment/selectors'

import { addNotify } from 'components/Notify'

interface IUseCreateTransfer {
  type: PaymentTypesType
  selectedSystem: string
  locale: LocalePaymentType
  onCloseCurrentPayment: () => void
}

export const useCreateTransfer = ({ type, locale, selectedSystem, onCloseCurrentPayment }: IUseCreateTransfer) => {
  const dispatch = useDispatch()

  const isDepositLoading = useSelector(selectCreateDepositComm, shallowEqual)?.[selectedSystem]?.isLoading
  const isPayoutLoading = useSelector(selectCreatePayoutComm, shallowEqual).isLoading
  const isConvertAmountLoading = useSelector(selectConvertedAmountDataComm, shallowEqual).isLoading
  const convertedAmountData = useSelector(selectConvertedAmountData, shallowEqual)
  const createdDeposit = useSelector(selectDepositData, shallowEqual)

  const [files, setFiles] = useState<File[] | null>(null)

  const successCallback = useCallback(() => {
    if (type === PaymentTypes.DEPOSIT) {
      addNotify(locale.depositCompleted, 'success')
    } else if (selectedSystem === mobCashSlug) {
      addNotify(locale.withdrawalRequestedCompleted, 'success')
    } else {
      addNotify(locale.withdrawalCompleted, 'success')
    }
    onCloseCurrentPayment()
  }, [type, selectedSystem, onCloseCurrentPayment, locale])

  const handleSubmit = useCallback(
    (values: IPaymentPayload) => {
      if (type === PaymentTypes.DEPOSIT) {
        dispatch(createDeposit({ ...values, files: files ?? [], successCallback }))
      } else {
        dispatch(createPayout({ ...values, successCallback }))
      }
    },
    [dispatch, files, successCallback, type],
  )

  const handleConvertAmount = useCallback(
    (data: IGetCryptoEstimateAmountPayload) => {
      dispatch(getCryptoEstimateAmount({ ...data }, { callback: successCallback }))
    },
    [dispatch, successCallback],
  )

  const handleClear = useCallback(() => {
    dispatch(clearDepositCreated())
    dispatch(clearConvertedAmountData())
    dispatch(clearScreenshotsData())
  }, [dispatch])

  const isLoading = useMemo(
    () => (type === PaymentTypes.DEPOSIT ? isDepositLoading : isPayoutLoading) || isConvertAmountLoading,
    [type, isDepositLoading, isPayoutLoading, isConvertAmountLoading],
  )

  const qrCodeData = useMemo(
    () => (createdDeposit?.qrCodeData ? createdDeposit?.qrCodeData : null),
    [createdDeposit?.qrCodeData],
  )

  useEffect(
    () => () => {
      handleClear()
    },
    [],
  )

  return {
    isLoading,
    isConvertAmountLoading,
    qrCodeData,
    convertedAmountData,
    files: files ?? [],
    setFiles,
    handleClear,
    convertAmount: handleConvertAmount,
    createTransfer: handleSubmit,
  }
}
